'use client';

import type { CSSProperties } from 'react';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { Fragment, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { handleAlertsSignup } from '@/app/hub/alerts/actions';
import { ALERTS_SIGNUP_DEFAULTS, type AlertsSignupValues } from '@/app/hub/alerts/schema';
import { HUB_EVENTS, pushDataLayer } from '@/lib/analytics';
import { useParallax } from '@/lib/hooks/useParallax';
import { parseUtmParams } from '@/lib/utm';
import { ArrowUpRight } from './icons';
import Reveal from './reveal';
import type { HubContent } from '@/lib/content/hub';
import type { CountryOption } from '@/lib/countries';

const INPUT_CLASS =
  'w-full min-h-[52px] border border-cream/25 bg-transparent px-4 py-3.5 text-[17px] text-cream placeholder:text-cream/35 focus:border-yamal focus:ring-1 focus:ring-yamal focus:outline-none';

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="m5 13 4 4L19 7" />
    </svg>
  );
}

function Spinner({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.3" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="hub-label-sm text-cream/70">{label}</span>
      <div className="mt-2">{children}</div>
      {error ?
        <span className="mt-1.5 block text-[15px] text-amber-300">{error}</span>
      : null}
    </label>
  );
}

type AlertsProps = {
  copy: HubContent['alerts'];
  locale: Locale;
  countries: CountryOption[];
  giveawayUrl: string;
};

type Status = 'idle' | 'sending' | 'done';

export default function Alerts({ copy, locale, countries, giveawayUrl }: AlertsProps) {
  const parallaxRef = useParallax<HTMLImageElement>(0.08, 10);
  const searchParams = useSearchParams();
  const utm = useMemo(
    () => parseUtmParams(Object.fromEntries(searchParams.entries())),
    [searchParams],
  );

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<AlertsSignupValues>({ defaultValues: ALERTS_SIGNUP_DEFAULTS });

  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState<string | null>(null);

  const onSubmit = handleSubmit(async (values) => {
    setStatus('sending');
    setFormError(null);
    try {
      const result = await handleAlertsSignup(values, locale, utm);
      if ('contactId' in result) {
        pushDataLayer(HUB_EVENTS.ALERTS_SUBMIT_SUCCESS, {});
        setStatus('done');
        return;
      }
      if (result.fieldErrors) {
        for (const [name, message] of Object.entries(result.fieldErrors)) {
          setError(name as keyof AlertsSignupValues, { message: message as string });
        }
      }
      setFormError(result.message || copy.genericError);
      pushDataLayer(HUB_EVENTS.ALERTS_SUBMIT_ERROR, {
        reason: result.fieldErrors ? 'validation' : 'server',
      });
      setStatus('idle');
    } catch {
      setFormError(copy.genericError);
      pushDataLayer(HUB_EVENTS.ALERTS_SUBMIT_ERROR, { reason: 'network' });
      setStatus('idle');
    }
  });

  const consentParts = copy.consent.split('{privacy}');
  const crossSellParts = copy.crossSell.split('{link}');

  return (
    <section id="alerts" className="hub-section bg-navy py-20 sm:py-28">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-16">
          <Reveal
            direction="left"
            className="relative hidden min-h-[820px] self-stretch overflow-hidden lg:block"
          >
            <Image
              src="/hub/alerts.jpg"
              alt={copy.imgAlt}
              fill
              sizes="(min-width: 1024px) 50vw, 0px"
              className="object-cover will-change-transform"
              style={
                {
                  '--parallax-y': '0px',
                  transform: 'translate3d(0, var(--parallax-y), 0) scale(1.02)',
                } as CSSProperties
              }
              ref={parallaxRef}
            />
            <Image
              src="/hub/live-your-city.png"
              alt=""
              aria-hidden="true"
              width={110}
              height={140}
              className="absolute right-8 bottom-8 h-auto w-[110px]"
            />
          </Reveal>

          <Reveal direction="right" delayMs={100}>
            <p className="hub-label-lg text-yamal">{copy.eyebrow}</p>
            <h2 className="hub-display mt-4 text-[clamp(2rem,5vw,3.4rem)] text-cream">
              {copy.headline}
            </h2>
            <p className="mt-5 max-w-md text-[17px] leading-relaxed text-cream/75">{copy.body}</p>

            {status === 'done' ?
              <div
                className="mt-8 border border-yamal/40 bg-yamal/10 p-8"
                role="status"
                aria-live="polite"
              >
                <div className="grid size-12 place-items-center rounded-full bg-yamal">
                  <CheckIcon className="size-6 text-coal" />
                </div>
                <p className="hub-display mt-5 text-3xl text-cream">{copy.doneTitle}</p>
                <p className="mt-3 max-w-prose text-[17px] leading-relaxed text-cream/75">
                  {copy.doneBody} {copy.doneSeparate}
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-5">
                  <a
                    href={giveawayUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() =>
                      pushDataLayer(HUB_EVENTS.GIVEAWAY_CLICK, { location: 'alerts_done' })
                    }
                    className="hub-display inline-flex min-h-[52px] items-center gap-2 bg-yamal px-6 py-4 text-[20px] tracking-[0.02em] text-coal transition-transform hover:-translate-y-0.5 active:translate-y-0"
                  >
                    {copy.enterDraw}
                    <ArrowUpRight className="size-5" />
                  </a>
                  <button
                    type="button"
                    onClick={() => {
                      reset(ALERTS_SIGNUP_DEFAULTS);
                      setStatus('idle');
                    }}
                    className="hub-label min-h-[44px] px-1 text-cream/70 underline underline-offset-4 transition-colors hover:text-yamal"
                  >
                    {copy.addAnother}
                  </button>
                </div>
              </div>
            : <form onSubmit={onSubmit} noValidate className="mt-8 space-y-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label={copy.firstName} error={errors.first_name?.message}>
                    <input
                      className={INPUT_CLASS}
                      autoComplete="given-name"
                      autoCapitalize="words"
                      {...register('first_name')}
                    />
                  </Field>
                  <Field label={copy.lastName} error={errors.last_name?.message}>
                    <input
                      className={INPUT_CLASS}
                      autoComplete="family-name"
                      autoCapitalize="words"
                      {...register('last_name')}
                    />
                  </Field>
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label={copy.email} error={errors.email?.message}>
                    <input
                      className={INPUT_CLASS}
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      placeholder={copy.emailPlaceholder}
                      {...register('email')}
                    />
                  </Field>
                  <Field label={copy.country} error={errors.country_of_residence?.message}>
                    <select
                      className={`${INPUT_CLASS} pr-11`}
                      defaultValue=""
                      autoComplete="country-name"
                      {...register('country_of_residence')}
                    >
                      <option value="" disabled>
                        {copy.countryPlaceholder}
                      </option>
                      {countries.map((country) => (
                        <option key={country.value} value={country.value} className="text-coal">
                          {country.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                <div className="pt-2">
                  <label className="flex cursor-pointer gap-3 text-[16px] leading-relaxed text-cream/75">
                    <input
                      type="checkbox"
                      className="mt-0.5 size-6 shrink-0 accent-yamal"
                      {...register('marketing_optin')}
                    />
                    <span>
                      {consentParts.map((part, index) => (
                        <Fragment key={index}>
                          {part}
                          {index < consentParts.length - 1 && (
                            <a
                              href={copy.privacyUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-yamal underline underline-offset-2"
                            >
                              {copy.privacyPolicy}
                            </a>
                          )}
                        </Fragment>
                      ))}
                    </span>
                  </label>
                  {errors.marketing_optin?.message ?
                    <p className="mt-1.5 text-[15px] text-amber-300">
                      {errors.marketing_optin.message}
                    </p>
                  : null}
                </div>

                {formError ?
                  <p className="text-[15px] text-amber-300" role="alert">
                    {formError}
                  </p>
                : null}

                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="hub-display inline-flex min-h-[52px] items-center gap-2 bg-yamal px-8 py-4 text-[21px] tracking-[0.02em] text-coal transition-transform hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60"
                >
                  {status === 'sending' ?
                    <Spinner className="size-[21px] animate-spin" />
                  : null}
                  {status === 'sending' ? copy.sending : copy.submit}
                </button>

                <p className="pt-1 text-[16px] text-cream/60">
                  {crossSellParts[0]}
                  <a
                    href={giveawayUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() =>
                      pushDataLayer(HUB_EVENTS.GIVEAWAY_CLICK, { location: 'alerts_crosssell' })
                    }
                    className="text-yamal underline underline-offset-2"
                  >
                    {copy.enterDraw}
                  </a>
                  {crossSellParts[1]}
                </p>
              </form>
            }
          </Reveal>
        </div>
      </div>
    </section>
  );
}
